import React, { useEffect } from 'react';
import {
    CheckCircle2,
    Image,
    Film,
    Play,
    Smartphone,
    Palette,
    Volume2,
    Type,
    Layers,
    SquarePlay,
    Clock,
    Send,
    Mic2,
    Sparkles,
    Package
} from 'lucide-react';

const Card = ({ children, className = "" }) => (
    <div className={`rounded-2xl border border-white/10 bg-white/5 backdrop-blur-lg p-6 md:p-8 shadow-xl ${className}`}>
        {children}
    </div>
);

const CheckRow = ({ children }) => (
    <li className="flex gap-3 items-start">
        <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0 text-indigo-400" aria-hidden />
        <span className="text-white/80 leading-relaxed">{children}</span>
    </li>
);

const DeliverableCard = ({ icon: IconComponent, number, title, items }) => (
    <Card className={`hover:border-indigo-500/40 transition-all duration-300`}>
        <div className="flex items-center gap-3 mb-5">
            <div className={`p-2.5 rounded-xl bg-indigo-500/20 text-indigo-400`}>
                <IconComponent className="w-6 h-6" />
            </div>
            <div>
                <span className={`text-xs font-bold uppercase tracking-widest text-indigo-400/60`}>
                    {String(number).padStart(2, '0')}
                </span>
                <h3 className="text-xl font-bold text-white leading-tight">{title}</h3>
            </div>
        </div>
        <ul className="space-y-3">
            {items.map((item, i) => (
                <CheckRow key={i}>{item}</CheckRow>
            ))}
        </ul>
    </Card>
);

export default function AbhiveerPodcast() {

    useEffect(() => {
        document.title = 'SocialSEO | Abhiveer Podcast Production';
    }, []);

    const deliverables = [
        {
            icon: Image,
            number: 1,
            title: "Thumbnail Design",
            items: [
                "3 unique thumbnail variants per episode to test different hooks, expressions, and layouts to maximize CTR",
                "Custom-designed, click-optimized compositions with guest face cutout, text overlay, and brand colors",
                "Exported at 1280×720 (YouTube standard) + high-res source files"
            ]
        },
        {
            icon: Film,
            number: 2,
            title: "Complete Podcast Render (4K)",
            items: [
                "Full episode rendered in 3840×2160 (4K UHD)",
                "Multi-camera sync and switching (if multi-cam)",
                "Clean cuts, removal of dead air, filler words, and technical errors",
                "Exported as .mp4 (H.265 for quality, H.264 for compatibility)"
            ]
        },
        {
            icon: Play,
            number: 3,
            title: "Intro Horizontal (16:9)",
            items: [
                "Branded intro bumper (5 to 15 seconds)",
                "Logo animation + sound design",
                "Episode-specific title card with guest name",
                "Seamless transition into episode content"
            ]
        },
        {
            icon: Smartphone,
            number: 4,
            title: "Intro Vertical (9:16)",
            items: [
                "Same intro adapted for Instagram Reels, YouTube Shorts, and TikTok",
                "Optimized text placement for mobile-first viewing",
                "1080×1920 export"
            ]
        },
        {
            icon: Palette,
            number: 5,
            title: "Color Grading and Correction",
            items: [
                "White balance correction across all camera angles",
                "Consistent color temperature between host and guest",
                "Cinematic LUT application matched to brand aesthetic",
                "Skin tone correction and exposure balancing",
                "Scene-to-scene color continuity"
            ]
        },
        {
            icon: Volume2,
            number: 6,
            title: "Audio Mastering",
            items: [
                "Background noise removal (hiss, hum, room reverb)",
                "Volume leveling between host and guest (loudness normalization to -14 LUFS for YouTube)",
                "Compression and EQ for vocal clarity",
                "De-essing (sibilance reduction)",
                "Consistent audio across the full episode"
            ]
        },
        {
            icon: Type,
            number: 7,
            title: "Lower Thirds and Graphics",
            items: [
                "Name and title cards for host and guest (first appearance + periodic)",
                "Topic title cards for segment transitions",
                "Social media handle overlays (intro/outro)",
                "Subscribe and like CTA graphics"
            ]
        },
        {
            icon: Layers,
            number: 8,
            title: "Transitions and B-Roll Integration",
            items: [
                "Smooth transitions between segments and topics",
                "Zoom-ins and camera angle switches for engagement",
                "B-roll overlay support (if footage provided)",
                "Text-on-screen for key quotes or statistics"
            ]
        },
        {
            icon: SquarePlay,
            number: 9,
            title: "Outro",
            items: [
                "Branded outro card (5 to 10 seconds)",
                "End screen layout optimized for YouTube (subscribe + next video)",
                "Social media links and CTA"
            ]
        }
    ];

    const requirements = [
        { item: "Raw footage", note: "All camera angles, highest quality available" },
        { item: "Audio files", note: "Separate audio tracks preferred (from lav mic / interface)" },
        { item: "Brand kit", note: "Logo (PNG/SVG), brand colors, fonts" },
        { item: "Guest info", note: "Full name, title, social handles, headshot" },
        { item: "Thumbnail direction", note: "Any specific style or text preferences" },
        { item: "Intro music / sound", note: "Licensed music or preference for style" }
    ];

    return (
        <div className="min-h-screen bg-transparent font-sans leading-relaxed text-white overflow-x-hidden selection:bg-indigo-500/30 pb-20 pt-24">

            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-50 p-6 flex justify-between items-center bg-transparent pointer-events-none">
                <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-4 py-2 rounded-full pointer-events-auto shadow-lg">
                    <span className="font-bold tracking-tight text-white">Social<span className="text-indigo-400">SEO</span></span>
                </div>
            </header>

            <main className="max-w-[1120px] mx-auto px-4 space-y-24">

                {/* Hero */}
                <section className="text-center pt-10">
                    <div className="inline-flex items-center gap-2 bg-indigo-500/10 border border-indigo-500/20 rounded-full px-4 py-1.5 mb-8">
                        <Mic2 className="w-4 h-4 text-indigo-400" />
                        <span className="text-sm font-medium text-indigo-300">Podcast Production Package</span>
                    </div>
                    <h1 className="text-5xl md:text-7xl font-black tracking-tight mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400">
                        SocialSEO × Abhiveer
                    </h1>
                    <p className="text-xl md:text-2xl text-white/70 max-w-3xl mx-auto leading-relaxed">
                        End-to-end podcast production. 4K render, color grading, audio mastering, branded intros, thumbnails, and everything in between.
                    </p>
                </section>

                {/* Scope-clarification banner */}
                <section className="max-w-3xl mx-auto -mt-12">
                    <div className="rounded-2xl border border-amber-400/30 bg-amber-400/5 px-6 py-5 text-center">
                        <p className="text-sm md:text-base text-amber-100/90 leading-relaxed">
                            <span className="font-semibold text-amber-200">Heads up — this is our editing-only package (v1).</span> For the full content-ops scope (trailers, ManyChat, multi-platform thumbnails, SEO captions, Later scheduling), see{' '}
                            <a href="/abhiveer-content-ops" className="underline decoration-amber-300/60 hover:decoration-amber-200 text-amber-50">
                                /abhiveer-content-ops
                            </a>.
                        </p>
                    </div>
                </section>

                {/* Pricing */}
                <section id="pricing">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-5xl font-bold mb-4">Simple Pricing</h2>
                        <p className="text-white/50 text-lg">Same deliverables. Same quality. Just commit to more and save.</p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                        {/* Single Episode */}
                        <Card className="flex flex-col border-t-4 border-t-slate-400 text-center">
                            <div className="mb-6 border-b border-white/10 pb-6">
                                <h3 className="text-2xl font-bold mb-2">Single Episode</h3>
                                <p className="text-white/50 text-sm">Pay per episode, no commitment</p>
                            </div>
                            <div className="mb-8">
                                <span className="text-5xl font-black text-white">$130</span>
                                <span className="text-white/50 font-medium ml-1">/ episode</span>
                            </div>
                            <div className="mt-auto">
                                <ul className="space-y-3 text-left">
                                    <CheckRow>All 9 deliverables included</CheckRow>
                                    <CheckRow>3 thumbnail variants</CheckRow>
                                    <CheckRow>7-day turnaround</CheckRow>
                                </ul>
                            </div>
                        </Card>

                        {/* 4-Episode Bundle */}
                        <Card className="flex flex-col border-t-4 border-t-indigo-500 bg-indigo-500/10 transform md:-translate-y-4 shadow-[0_0_50px_-15px_rgba(99,102,241,0.3)] relative text-center">
                            <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-indigo-500 text-white text-xs font-bold px-4 py-1 rounded-full uppercase tracking-widest shadow-lg flex items-center gap-1">
                                Best Value <Sparkles className="w-3 h-3" />
                            </div>
                            <div className="mb-6 border-b border-white/10 pb-6 mt-2">
                                <h3 className="text-2xl font-bold mb-2">4-Episode Bundle</h3>
                                <p className="text-white/50 text-sm">Commit to 4 episodes, save 23%</p>
                            </div>
                            <div className="mb-2">
                                <span className="text-5xl font-black text-white">$100</span>
                                <span className="text-white/50 font-medium ml-1">/ episode</span>
                            </div>
                            <div className="mb-8">
                                <div className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/20 rounded-full px-3 py-1 mt-2">
                                    <span className="text-sm font-bold text-green-400">$400 total, save $120</span>
                                </div>
                            </div>
                            <div className="mt-auto">
                                <ul className="space-y-3 text-left">
                                    <CheckRow>All 9 deliverables included</CheckRow>
                                    <CheckRow>3 thumbnail variants</CheckRow>
                                    <CheckRow>7-day turnaround</CheckRow>
                                </ul>
                            </div>
                        </Card>
                    </div>
                </section>

                {/* 3 Thumbnail Highlight */}
                <section>
                    <Card className="max-w-4xl mx-auto border-amber-500/20 bg-gradient-to-br from-amber-500/10 to-amber-600/5 relative overflow-hidden">
                        <div className="absolute -right-6 -top-6 text-[120px] font-black text-amber-500/[0.06] pointer-events-none leading-none">3×</div>
                        <div className="flex items-start gap-5">
                            <div className="bg-amber-500/20 p-3 rounded-2xl flex-shrink-0 mt-1">
                                <Image className="w-8 h-8 text-amber-400" />
                            </div>
                            <div>
                                <h3 className="text-2xl md:text-3xl font-black text-white mb-3">
                                    3 Thumbnail Variants. Every Episode.
                                </h3>
                                <p className="text-white/70 text-lg leading-relaxed mb-4">
                                    Most editors hand you one thumbnail and call it done. We deliver <strong className="text-amber-300">three unique compositions per episode</strong> with different angles, text hooks, and layouts so you can A/B/C test and let the data pick the winner.
                                </p>
                                <div className="grid grid-cols-3 gap-3 mt-4">
                                    <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                                        <span className="text-2xl font-black text-amber-400">A</span>
                                        <p className="text-xs text-white/50 mt-1">Bold Hook</p>
                                    </div>
                                    <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                                        <span className="text-2xl font-black text-amber-400">B</span>
                                        <p className="text-xs text-white/50 mt-1">Guest Focus</p>
                                    </div>
                                    <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                                        <span className="text-2xl font-black text-amber-400">C</span>
                                        <p className="text-xs text-white/50 mt-1">Minimal Clean</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Card>
                </section>

                {/* What's Included — Full Deliverables */}
                <section>
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-5xl font-bold mb-4 flex items-center justify-center gap-3">
                            <Package className="w-8 h-8 text-indigo-400" />
                            What's Included
                        </h2>
                        <p className="text-white/50 text-lg max-w-2xl mx-auto">
                            Every episode ships with 10 production deliverables. No hidden fees, no premium add-ons.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                        {deliverables.map((d, i) => (
                            <DeliverableCard key={i} {...d} />
                        ))}
                    </div>
                </section>

                {/* Turnaround */}
                <section>
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold flex items-center justify-center gap-3">
                            <Clock className="w-8 h-8 text-indigo-400" />
                            Turnaround and Workflow
                        </h2>
                    </div>

                    <Card className="max-w-3xl mx-auto">
                        <div className="space-y-0">
                            {[
                                { day: "Day 0", label: "Raw files received", color: "bg-slate-500" },
                                { day: "Day 3 to 5", label: "First draft delivery", color: "bg-indigo-500" },
                                { day: "Day 7", label: "Final delivery", color: "bg-green-500" }
                            ].map((step, i, arr) => (
                                <div key={i} className="flex items-center gap-4 py-4">
                                    <div className="flex flex-col items-center w-16 flex-shrink-0">
                                        <div className={`w-3 h-3 rounded-full ${step.color}`} />
                                        {i < arr.length - 1 && (
                                            <div className="w-0.5 h-8 bg-white/10 mt-1" />
                                        )}
                                    </div>
                                    <div className="flex-grow">
                                        <span className="text-sm font-bold text-white/50 uppercase tracking-widest">{step.day}</span>
                                        <p className="text-lg font-semibold text-white">{step.label}</p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="border-t border-white/10 mt-4 pt-6 space-y-3">
                            <div className="flex justify-between items-center">
                                <span className="text-white/60">Rush delivery (48hr)</span>
                                <span className="font-bold text-amber-400">+25% surcharge</span>
                            </div>
                        </div>
                    </Card>
                </section>

                {/* What We Need From You */}
                <section className="pb-10">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold flex items-center justify-center gap-3">
                            <Send className="w-8 h-8 text-indigo-400" />
                            What We Need From You
                        </h2>
                    </div>

                    <Card className="max-w-3xl mx-auto">
                        <div className="space-y-0 divide-y divide-white/10">
                            {requirements.map((req, i) => (
                                <div key={i} className="flex items-start justify-between py-4 gap-4">
                                    <div className="flex items-center gap-3">
                                        <span className="bg-indigo-500/20 text-indigo-400 w-7 h-7 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0">
                                            {i + 1}
                                        </span>
                                        <span className="font-semibold text-white">{req.item}</span>
                                    </div>
                                    <span className="text-white/50 text-sm text-right">{req.note}</span>
                                </div>
                            ))}
                        </div>
                    </Card>
                </section>

            </main>
        </div>
    );
}
